import type { LibraryPageContent } from "./types";

/**
 * Technical documentation (/docs).
 *
 * Documentation lives with the code it documents and is versioned with it, so
 * this page is a signpost rather than a copy. Duplicating install instructions
 * into marketing content guarantees they go stale, and stale install
 * instructions are worse than a click through to the repository.
 *
 * It reuses the library template: the shape is identical — a filtered list of
 * things that exist elsewhere — and a second near-identical template would
 * drift from the first.
 */
export const DOCS_PAGE: LibraryPageContent = {
  path: "/docs",
  eyebrow: "Resources",
  headline: "Documentation lives with the code",
  lede:
    "Install guides, API reference and deployment notes are versioned alongside the packages they describe, so they stay correct. These are the entry points.",
  filters: [
    { id: "runtime", label: "Runtime security" },
    { id: "redteam", label: "Red teaming" },
    { id: "ci", label: "CI and tooling" },
    { id: "governance", label: "Governance and compliance" },
  ],
  entries: [
    {
      type: "runtime",
      title: "cerberus-core",
      blurb:
        "The open core: guard() around your tool executor, the detection layers, and the provenance ledger. Installation, configuration and framework adapters.",
      href: "https://github.com/Odingard/cerberus-core",
      meta: "MIT · GitHub",
      external: true,
    },
    {
      type: "runtime",
      title: "@cerberus-ai/core on npm",
      blurb:
        "The published package. Runtime security for AI agent tool execution — detects, correlates and interrupts guarded Lethal Trifecta attack paths.",
      href: "https://www.npmjs.com/package/@cerberus-ai/core",
      meta: "npm",
      external: true,
    },
    {
      type: "runtime",
      title: "Cerberus product page",
      blurb:
        "What the runtime does, the editions, and the measured evidence with its methodology stated alongside it.",
      href: "/cerberus",
      meta: "Product",
    },
    {
      type: "redteam",
      title: "argus-core",
      blurb:
        "The open core of the autonomous red-team engine for LLM and agent targets.",
      href: "https://github.com/Odingard/argus-core",
      meta: "MIT · GitHub",
      external: true,
    },
    {
      type: "redteam",
      title: "ARGUS validation benchmarks",
      blurb:
        "Nineteen intentionally vulnerable agent targets with canary-based win conditions, for evaluating offensive tooling against something other than your own product.",
      href: "https://github.com/Odingard/validation-benchmarks",
      meta: "Apache-2.0 · GitHub",
      external: true,
    },
    {
      type: "redteam",
      title: "ttp-lab",
      blurb:
        "Reproduce the published read-relevance gate study from seed, or run the mechanism over your own agent-memory traces.",
      href: "https://github.com/Odingard/ttp-lab",
      meta: "MIT · GitHub",
      external: true,
    },
    {
      type: "ci",
      title: "cerberus-action",
      blurb:
        "A GitHub Action that tests one agent workflow for dangerous tool-execution paths on every pull request.",
      href: "https://github.com/Odingard/cerberus-action",
      meta: "MIT · GitHub Action",
      external: true,
    },
    {
      type: "ci",
      title: "cerberus-mcp-tool-scan",
      blurb:
        "A GitHub Action that scans agent and MCP tool descriptions for hidden or malicious instructions before they reach a model.",
      href: "https://github.com/Odingard/cerberus-mcp-tool-scan",
      meta: "MIT · GitHub Action",
      external: true,
    },
    {
      type: "governance",
      title: "TraceLock product page",
      blurb:
        "Continuous control monitoring, the framework crosswalk, and what an auditor can verify independently.",
      href: "/tracelock",
      meta: "Product",
    },
    {
      type: "governance",
      title: "Glossary",
      blurb:
        "Definitions for the terms used across the documentation and the product pages.",
      href: "/glossary",
      meta: "Reference",
    },
  ],
  close: {
    heading: "Need help getting it into production?",
    body:
      "Warden's deployment practice does the runtime rollout, the integration work and the enablement — the same engineers who maintain these packages.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=docs" },
      {
        label: "Cerberus Runtime Deployment",
        href: "/warden/cerberus-deployment",
      },
    ],
  },
};
